import { Directive, ElementRef, computed, input, inject } from '@angular/core';

/**
 * E2eIdDirective — sets a stable `data-e2e-id` for testing.
 *
 * Inputs:
 * - `lcTestId: string | string[] | null`  // preferred
 * - `e2eSticky: boolean`                  // freeze first value (default: true)
 * - `e2eIncludeComponentTag: boolean`     // prepend nearest `lc-*` tag or "root" (default: true)
 *
 * Fallback:
 * - If no lcTestId, auto-generate `${nearest lc-* tag}:element-N`
 *
 * Examples:
 * <button [lcTestId]="'continue'"></button>              <!-- checkout:continue -->
 * <button [lcTestId]="['checkout','continue']"></button> <!-- checkout:continue -->
 * <div lcTestId></div>                                   <!-- lc-card:element-1 -->
 */
@Directive({
  selector: '[lcTestId]',
  host: { '[attr.data-e2e-id]': 'e2eId()' },
})
export class E2eIdDirective {
  lcTestId = input<string | string[] | null>(null);
  e2eSticky = input<boolean>(true);
  e2eIncludeComponentTag = input<boolean>(true);

  private elementRef = inject<ElementRef<Element>>(ElementRef);
  private frozen: string | null = null;

  private static counters = new Map<string, number>();

  e2eId = computed((): string | null => this.computeE2eId());

  private computeE2eId(): string | null {
    const host = this.elementRef.nativeElement;
    if (!host) return null;
    if (this.e2eSticky() && this.frozen) return this.frozen;

    const locals = this.resolveLocals(host);
    if (locals.length === 0) return null;

    const parts: string[] = this.e2eIncludeComponentTag()
      ? [this.nearestComponentTag(host), ...locals]
      : locals;

    const id = parts.join(':');
    if (this.e2eSticky() && !this.frozen) this.frozen = id;
    return id;
  }

  private resolveLocals(host: Element): string[] {
    const raw = this.lcTestId();
    let values: string[] = [];
    if (Array.isArray(raw)) {
      values = raw;
    } else if (raw) {
      values = [raw];
    }
    const normalized = values.map((v) => this.kebab(v)).filter(Boolean);
    if (normalized.length > 0) return normalized;

    const tag = this.nearestComponentTag(host);
    const idx = this.nextIndex(tag);
    return [`element-${idx}`];
  }

  private nearestComponentTag(el: Element): string {
    let node: Element | null = el.parentElement;
    while (node) {
      const tag = node.tagName.toLowerCase();
      if (tag.startsWith('lc-')) return tag;
      node = node.parentElement;
    }
    return 'root';
  }

  private nextIndex(tag: string): number {
    const current = E2eIdDirective.counters.get(tag) ?? 0;
    const next = current + 1;
    E2eIdDirective.counters.set(tag, next);
    return next;
  }

  private kebab(text: unknown): string {
    if (text == null) return '';
    const s = String(text).trim();
    return s ? s.replace(/[^A-Za-z0-9]+/g, ' ').trim().toLowerCase().replace(/\s+/g, '-') : '';
  }
}
